import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Loader2,
} from 'lucide-react';
import PDFFieldOverlay from './PDFFieldOverlay';

/**
 * Affiche une page d'un document pdf.js avec une overlay de champs superposée.
 *
 * Props :
 *  - pdfDoc        : PDFDocumentProxy (pdf.js)
 *  - currentPage   : number (1-based)
 *  - totalPages    : number
 *  - onPageChange  : (fn) => void
 *  - pageFields    : champs pdf_overlay pour la page courante
 *  - annMap        : { fieldKey -> annotation }
 *  - formData      : data_json courant
 *  - onFieldChange : (newFormData) => void
 *  - isMJ          : bool
 *  - isReadOnly    : bool
 */
export default function PDFPageViewer({
  pdfDoc,
  currentPage,
  totalPages,
  onPageChange,
  pageFields,
  annMap,
  formData,
  onFieldChange,
  isMJ,
  isReadOnly,
}) {
  const canvasRef    = useRef(null);
  const containerRef = useRef(null);
  const renderTaskRef = useRef(null);

  const [zoom, setZoom]         = useState(1.0);
  const [rendering, setRendering] = useState(false);

  // ── Rendu de la page ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!pdfDoc || !canvasRef.current || !containerRef.current) return;

    // Annuler le rendu en cours si besoin
    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
      renderTaskRef.current = null;
    }

    let cancelled = false;
    setRendering(true);

    (async () => {
      try {
        const page = await pdfDoc.getPage(currentPage);
        if (cancelled) return;

        const containerWidth = containerRef.current?.clientWidth || 600;
        const viewport       = page.getViewport({ scale: 1 });
        const scale          = (containerWidth / viewport.width) * zoom;
        const scaledViewport = page.getViewport({ scale });

        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width  = scaledViewport.width;
        canvas.height = scaledViewport.height;

        const ctx = canvas.getContext('2d');
        const renderTask = page.render({ canvasContext: ctx, viewport: scaledViewport });
        renderTaskRef.current = renderTask;

        await renderTask.promise;
        renderTaskRef.current = null;
      } catch (err) {
        if (err?.name !== 'RenderingCancelledException') {
          console.error('[PDFPageViewer] render error:', err);
        }
      } finally {
        if (!cancelled) setRendering(false);
      }
    })();

    return () => {
      cancelled = true;
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [pdfDoc, currentPage, zoom]);

  // ── Handlers zoom ─────────────────────────────────────────────────────────
  const handleZoomOut   = () => setZoom((z) => Math.max(0.5, parseFloat((z - 0.25).toFixed(2))));
  const handleZoomIn    = () => setZoom((z) => Math.min(2.0, parseFloat((z + 0.25).toFixed(2))));
  const handleZoomReset = () => setZoom(1.0);

  return (
    <div className="flex flex-col gap-3">

      {/* ── Barre navigation + zoom ─────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-2">

        {/* Pagination */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            disabled={currentPage <= 1 || rendering}
            onClick={() => onPageChange((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground tabular-nums px-1">
            {currentPage} / {totalPages}
          </span>
          <Button
            variant="ghost"
            size="icon"
            disabled={currentPage >= totalPages || rendering}
            onClick={() => onPageChange((p) => Math.min(totalPages, p + 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Zoom */}
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={handleZoomOut} disabled={zoom <= 0.5}>
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-xs w-10 text-center tabular-nums text-muted-foreground">
            {Math.round(zoom * 100)}%
          </span>
          <Button variant="ghost" size="icon" onClick={handleZoomReset} title="100%">
            <RotateCcw className="h-3 w-3" />
          </Button>
          <Button variant="ghost" size="icon" onClick={handleZoomIn} disabled={zoom >= 2.0}>
            <ZoomIn className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* ── Canvas + overlay ─────────────────────────────────────────────────── */}
      <div
        ref={containerRef}
        className="relative border border-border rounded-lg bg-muted/10 overflow-auto"
        style={{ maxHeight: 'calc(100vh - 260px)' }}
      >
        <div className="relative inline-block">
          {/* Spinner pendant le rendu */}
          {rendering && (
            <div
              className="absolute inset-0 flex items-center justify-center bg-background/60 z-10"
              style={{ minWidth: 200, minHeight: 200 }}
            >
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}

          {/* Canvas pdf.js */}
          <canvas ref={canvasRef} className="block" style={{ display: 'block' }} />

          {/* Overlay des champs (masqué pendant le rendu pour éviter décalages) */}
          {!rendering && (
            <PDFFieldOverlay
              pageFields={pageFields}
              annMap={annMap}
              formData={formData}
              onFieldChange={onFieldChange}
              isMJ={isMJ}
              isReadOnly={isReadOnly}
            />
          )}
        </div>
      </div>
    </div>
  );
}
